import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialSettings() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance/settings`;

  const [tab, setTab] = useState('general');
  const [settings, setSettings] = useState(null);
  const [taxRates, setTaxRates] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [txnCategories, setTxnCategories] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [payerTypes, setPayerTypes] = useState([]);
  const [payeeTypes, setPayeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const endpoints = {
    'tax-rates': { get: 'tax-rates', state: taxRates, setState: setTaxRates, fields: ['name', 'rate', 'type', 'is_default'] },
    'payment-methods': { get: 'payment-methods', state: paymentMethods, setState: setPaymentMethods, fields: ['name', 'code', 'is_active', 'sort_order'] },
    'accounts': { get: 'financial-accounts', state: accounts, setState: setAccounts, fields: ['name', 'type', 'account_number', 'bank_name', 'opening_balance', 'is_active'] },
    'txn-categories': { get: 'transaction-categories', state: txnCategories, setState: setTxnCategories, fields: ['name', 'type', 'sort_order'] },
    'project-types': { get: 'project-types', state: projectTypes, setState: setProjectTypes, fields: ['name', 'description'] },
    'payer-types': { get: 'payer-types', state: payerTypes, setState: setPayerTypes, fields: ['name', 'description'] },
    'payee-types': { get: 'payee-types', state: payeeTypes, setState: setPayeeTypes, fields: ['name', 'description'] },
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sett, tr, pm, fa, tc, pt, pyt, pet] = await Promise.all([
        fetch(`${base}`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${base}/tax-rates`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/payment-methods`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/financial-accounts`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/transaction-categories`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/project-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/payer-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${base}/payee-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      ]);
      setSettings(sett);
      setTaxRates(Array.isArray(tr) ? tr : []);
      setPaymentMethods(Array.isArray(pm) ? pm : []);
      setAccounts(Array.isArray(fa) ? fa : []);
      setTxnCategories(Array.isArray(tc) ? tc : []);
      setProjectTypes(Array.isArray(pt) ? pt : []);
      setPayerTypes(Array.isArray(pyt) ? pyt : []);
      setPayeeTypes(Array.isArray(pet) ? pet : []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  const updateSettings = async () => {
    const res = await fetch(`${base}`, { method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(settings) });
    if (res.ok) { const d = await res.json(); setSettings(d); }
  };

  const seedDefaults = async () => {
    if (!window.confirm('Seed default values? This will add standard payment methods, categories, and types.')) return;
    const res = await fetch(`${base}/seed-defaults`, { method: 'POST', headers: getAuthHeaders() });
    if (res.ok) fetchAll();
  };

  const saveItem = async (endpoint) => {
    const ep = endpoints[endpoint];
    const url = editing ? `${base}/${ep.get}/${editing.id}` : `${base}/${ep.get}`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { setShowForm(false); setEditing(null); setForm({}); fetchAll(); }
  };

  const openForm = (endpoint, item) => {
    if (item) {
      setEditing(item);
      setForm({ ...item });
    } else {
      setEditing(null);
      setForm({});
    }
    setShowForm(endpoint);
  };

  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  const tabs = [
    { key: 'general', label: '⚙️ General' },
    { key: 'tax-rates', label: '💰 Tax Rates' },
    { key: 'payment-methods', label: '💳 Pay Methods' },
    { key: 'accounts', label: '🏦 Accounts' },
    { key: 'txn-categories', label: '📂 Txn Categories' },
    { key: 'project-types', label: '📁 Project Types' },
    { key: 'payer-types', label: '👤 Payer Types' },
    { key: 'payee-types', label: '🏢 Payee Types' },
  ];

  const renderTable = (endpoint, columns) => {
    const ep = endpoints[endpoint];
    const items = ep.state;
    return (
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{tabs.find(t => t.key === endpoint)?.label}</h3>
          <button onClick={() => openForm(endpoint, null)} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>+ Add</button>
        </div>
        {items.length === 0 ? <div style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>No items. Click "Add" to create one, or use "Seed Defaults".</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{columns.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}<th style={thStyle}>Actions</th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    {columns.map(c => <td key={c.key} style={tdStyle}>{c.render ? c.render(item[c.key], item) : (item[c.key] ?? '-')}</td>)}
                    <td style={tdStyle}><button onClick={() => openForm(endpoint, item)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>✏️</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const boolBadge = (v) => v ? '✅' : '❌';

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>⚙️ Financial Settings</h2>
        <button onClick={seedDefaults} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff' }}>🌱 Seed Defaults</button>
      </div>

      {/* Tab Nav */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...btnStyle, background: tab === t.key ? '#3b82f6' : '#f1f5f9', color: tab === t.key ? '#fff' : '#374151', fontSize: '0.8rem', padding: '6px 12px' }}>{t.label}</button>
        ))}
      </div>

      {/* General Settings */}
      {tab === 'general' && settings && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: 600 }}>General Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 600 }}>
            <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Default Currency</label><input value={settings.default_currency || ''} onChange={e => setSettings({ ...settings, default_currency: e.target.value })} style={inputStyle} /></div>
            <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Fiscal Year Start</label><input type="date" value={settings.fiscal_year_start || ''} onChange={e => setSettings({ ...settings, fiscal_year_start: e.target.value })} style={inputStyle} /></div>
            <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Default Tax Rate ID</label><input type="number" value={settings.default_tax_rate_id || ''} onChange={e => setSettings({ ...settings, default_tax_rate_id: e.target.value })} style={inputStyle} /></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 16 }}>
              <input type="checkbox" checked={settings.require_approval_for_payables || false} onChange={e => setSettings({ ...settings, require_approval_for_payables: e.target.checked })} />
              <label style={{ fontSize: '0.85rem' }}>Require Approval for Payables</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={settings.auto_generate_references || false} onChange={e => setSettings({ ...settings, auto_generate_references: e.target.checked })} />
              <label style={{ fontSize: '0.85rem' }}>Auto-generate References</label>
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <button onClick={updateSettings} style={{ ...btnStyle, background: '#22c55e', color: '#fff' }}>💾 Save Settings</button>
          </div>
        </div>
      )}

      {/* Tax Rates */}
      {tab === 'tax-rates' && renderTable('tax-rates', [
        { key: 'name', label: 'Name' },
        { key: 'rate', label: 'Rate' },
        { key: 'type', label: 'Type' },
        { key: 'is_default', label: 'Default', render: boolBadge },
      ])}

      {/* Payment Methods */}
      {tab === 'payment-methods' && renderTable('payment-methods', [
        { key: 'name', label: 'Name' },
        { key: 'code', label: 'Code' },
        { key: 'sort_order', label: 'Order' },
        { key: 'is_active', label: 'Active', render: boolBadge },
      ])}

      {/* Financial Accounts */}
      {tab === 'accounts' && renderTable('accounts', [
        { key: 'name', label: 'Name' },
        { key: 'type', label: 'Type' },
        { key: 'account_number', label: 'Account #' },
        { key: 'bank_name', label: 'Bank' },
        { key: 'opening_balance', label: 'Opening Balance', render: (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
        { key: 'is_active', label: 'Active', render: boolBadge },
      ])}

      {/* Transaction Categories */}
      {tab === 'txn-categories' && renderTable('txn-categories', [
        { key: 'name', label: 'Name' },
        { key: 'type', label: 'Type', render: (v) => v === 'incoming' ? '📥 Incoming' : '📤 Outgoing' },
        { key: 'sort_order', label: 'Order' },
      ])}

      {/* Project Types */}
      {tab === 'project-types' && renderTable('project-types', [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
      ])}

      {/* Payer Types */}
      {tab === 'payer-types' && renderTable('payer-types', [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
      ])}

      {/* Payee Types */}
      {tab === 'payee-types' && renderTable('payee-types', [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
      ])}

      {/* Generic Edit/Create Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 500, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 20px' }}>{editing ? 'Edit' : 'New'} {tabs.find(t => t.key === showForm)?.label || ''}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {showForm === 'tax-rates' && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Rate (%)</label><input type="number" step="0.01" value={form.rate || ''} onChange={e => setForm({ ...form, rate: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.type || 'percentage'} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}><option value="percentage">Percentage</option><option value="fixed">Fixed</option></select></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_default || false} onChange={e => setForm({ ...form, is_default: e.target.checked })} /><label style={{ fontSize: '0.85rem' }}>Default Tax Rate</label></div>
                </>
              )}
              {showForm === 'payment-methods' && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Code</label><input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Sort Order</label><input type="number" value={form.sort_order || ''} onChange={e => setForm({ ...form, sort_order: e.target.value })} style={inputStyle} /></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({ ...form, is_active: e.target.checked })} /><label style={{ fontSize: '0.85rem' }}>Active</label></div>
                </>
              )}
              {showForm === 'accounts' && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.type || 'bank'} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}><option value="bank">Bank</option><option value="cash">Cash</option><option value="wallet">Wallet</option><option value="other">Other</option></select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Account Number</label><input value={form.account_number || ''} onChange={e => setForm({ ...form, account_number: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Bank Name</label><input value={form.bank_name || ''} onChange={e => setForm({ ...form, bank_name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Opening Balance</label><input type="number" step="0.01" value={form.opening_balance || ''} onChange={e => setForm({ ...form, opening_balance: e.target.value })} style={inputStyle} /></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({ ...form, is_active: e.target.checked })} /><label style={{ fontSize: '0.85rem' }}>Active</label></div>
                </>
              )}
              {showForm === 'txn-categories' && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.type || 'incoming'} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}><option value="incoming">Incoming</option><option value="outgoing">Outgoing</option></select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Sort Order</label><input type="number" value={form.sort_order || ''} onChange={e => setForm({ ...form, sort_order: e.target.value })} style={inputStyle} /></div>
                </>
              )}
              {(showForm === 'project-types' || showForm === 'payer-types' || showForm === 'payee-types') && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Description</label><textarea rows={3} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); setForm({}); }} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
              <button onClick={() => saveItem(showForm)} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
